import * as React from "react";

import { Syllabus, Clazz, Move, User } from "../types";
import Layout from "../components/common/Layout";
import useListSyllabusMoves from "../hooks/syllabi/useListSyllabusMoves";
import List from "../components/common/List";
import Text from "../components/common/Text";
import {
  Info,
  JustifyContent,
  Space,
  Surface,
  AlignItems,
} from "../styled-component";
import MoveComponents from "../components/move";
import AddMoveToSyllabus from "../components/syllabus/AddMoveToSyllabus";
import useFetchSyllabus from "../hooks/syllabi/useFetchSyllabus";
import AppContext from "../app-context/AppContext";
import StudentProgressComponents from "../components/studentProgress";
import Card from "../components/common/Card";
import { ScrollView } from "react-native";
import useFetchUser from "../hooks/users/useFetchUser";
import CreateOrUpdateSyllabus from "../components/syllabus/CreateOrUpdateSyllabus";
import Button from "../components/common/PressableButton";
import { useQueryClient } from "react-query";
import UserDisplayName from "../components/user/UserDisplayName";
import Icon from "../common/components/Icon";
import useCRUD from "../common/hooks/useCRUD";
import SyllabusService from "../services/Syllabus";

export interface ClazzSyllabusCardProps {
  clazzId: Clazz["id"];
  syllabusId: Syllabus["id"];
  learnerId?: User["id"];
  greedy?: boolean;
  onPressLearner?: () => void;
}

const service = new SyllabusService();

function ClazzSyllabusCard({
  syllabusId,
  learnerId,
  greedy,
  onPressLearner,
}: ClazzSyllabusCardProps) {
  // const { data: syllabus } = useFetchSyllabus(syllabusId);
  const movesQuery = useListSyllabusMoves(syllabusId);
  const { data: learner } = useFetchUser(learnerId);
  const queryClient = useQueryClient();

  const {
    data: syllabus,
    update,
    isLoading,
  } = useCRUD<Syllabus, { moves: Move["id"][] }>({
    id: syllabusId,
    service,
  });
  const moveIds = React.useMemo(
    () => syllabus?.moves?.map((move) => move.id),
    [syllabus]
  );

  console.log("syllabus = ", syllabus);

  return (
    <Card
      greedy={greedy}
      header={
        <Card.Header
          title={syllabus?.name}
          label="Syllabus"
          right={
            <AppContext.Instructor>
              <CreateOrUpdateSyllabus id={syllabusId}>
                {({ onPress, title }) => (
                  <Button title={title} onPress={onPress} color="accent" />
                )}
              </CreateOrUpdateSyllabus>
            </AppContext.Instructor>
          }
        />
      }
    >
      <AppContext.Instructor>
        <Card.SubHeader
          left={
            <Layout.Box size={1}>
              <Button onPress={onPressLearner}>
                {({ color }) => (
                  <Layout.Row alignItems={AlignItems.Center}>
                    {learner && (
                      <>
                        <Icon name="times" color={color} />

                        <Space />
                      </>
                    )}

                    <Button.Text color={color}>
                      {learner ? (
                        <UserDisplayName user={learner} />
                      ) : (
                        "Select Learner"
                      )}
                    </Button.Text>
                  </Layout.Row>
                )}
              </Button>
            </Layout.Box>
          }
          right={
            !learnerId && (
              <Layout.Box size={1}>
                <AddMoveToSyllabus syllabusId={syllabusId} />
              </Layout.Box>
            )
          }
        />

        <Space />
      </AppContext.Instructor>

      <Layout.Row greedy>
        <Space />

        <Info greedy>
          <ScrollView>
            <Layout.Column size={0.5} alignItems={AlignItems.Center}>
              <List.Async<Move>
                {...movesQuery}
                renderEmpty={() => (
                  <Layout.Box fullWidth alignItems="center">
                    <AppContext.Instructor>
                      {learnerId ? (
                        <Text textAlign="center">No moves</Text>
                      ) : (
                        <Text textAlign="center">
                          Press 'Add Move' to add a move
                        </Text>
                      )}
                    </AppContext.Instructor>

                    <AppContext.Learner>
                      <Text textAlign="center">No moves</Text>
                    </AppContext.Learner>
                  </Layout.Box>
                )}
                renderItem={({ item }: { item: Move; index: number }) => {
                  const move = (
                    <Layout.Box size={1}>
                      <Layout.Row alignItems="flex-start">
                        <MoveComponents.One move={item} />

                        <AppContext.Learner or={!!learnerId}>
                          <Space />

                          <StudentProgressComponents.Input
                            fullHeight
                            syllabusId={syllabusId}
                            moveId={item.id}
                            learnerId={learnerId}
                            disabled={!!learnerId}
                            justifyContent={JustifyContent.Center}
                          />
                        </AppContext.Learner>
                      </Layout.Row>
                    </Layout.Box>
                  );

                  const moveButton = (
                    <Button
                      onPress={() => {
                        if (!learner) {
                          update({
                            id: syllabusId,
                            moves: moveIds?.filter(
                              (moveId) => moveId !== item.id
                            ),
                          });
                        }
                      }}
                    >
                      {move}
                    </Button>
                  );

                  return (
                    <Layout.Box size={0.5}>
                      <Card>{learnerId ? move : moveButton}</Card>
                    </Layout.Box>
                  );
                }}
              />
            </Layout.Column>
          </ScrollView>
        </Info>

        <Space />
      </Layout.Row>

      <Space />
    </Card>
  );
}

export default ClazzSyllabusCard;
